import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useCFContributions } from '../hooks/useCFContributions';
import { useCFSavedSearches } from '../hooks/useCFSavedSearches';
import {
  CFFilters,
  CFSavedFilters,
  CFSummaryCards,
  CFTrendChart,
  CFCommitteeDashboard,
  CFContributorDashboard,
  CFVendorDashboard,
  CFExplorerPanel,
} from '../components/campaign-finance';
import { useCurrentElectionCycle } from '../hooks/useCurrentElectionCycle';
import SaveFilterModal, { type SaveFilterPreviewItem } from '../components/SaveFilterModal';
import type { CFQuery, SavedCFSearch } from '../types/campaignFinance';
import { getPageMode, urlParamsToQuery, queryToUrlParams } from '../utils/campaignFinanceUtils';

const CampaignFinancePage: React.FC = () => {
  usePageTitle('Campaign Finance', 'Colorado Secretary of State campaign finance filings');

  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState<CFQuery>(() => urlParamsToQuery(searchParams.toString()));
  // Snapshot of the query that produced the currently displayed results. The live
  // `query` updates on every keystroke; results-section UI (recipient name, mode,
  // empty-state hints) must reflect the last *searched* query so they don't drift
  // ahead of the data before the user hits Search.
  const [searchedQuery, setSearchedQuery] = useState<CFQuery>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [criteriaRequired, setCriteriaRequired] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);

  const {
    contributions,
    error, summary, monthlyTrend, topRecipients,
    search,
  } = useCFContributions();

  const { savedSearches, loading: savedLoading, save: saveSearch, remove: removeSearch, updateRun } = useCFSavedSearches();
  const { cycle } = useCurrentElectionCycle();

  const mode = useMemo(() => getPageMode(searchedQuery), [searchedQuery]);

  // Intentionally NO auto-search on mount. The form restores its filters from the
  // URL (query is seeded from urlParamsToQuery above), but nothing is fetched until
  // the user presses Search — a cycle-only auto-run over the full dataset times out.

  // The URL mirrors the last *executed* search, one history entry per search, so
  // the browser Back button steps back through searches instead of off the page.
  // (Form edits deliberately do NOT rewrite the URL — a keystroke-level replace
  // would overwrite the previous search's entry and make Back a no-op.)
  // lastParamsRef tracks our own URL writes so the effect below can tell them
  // apart from browser back/forward navigation.
  const lastParamsRef = useRef<string>(searchParams.toString());

  const doSearch = useCallback((q: CFQuery) => {
    const hasFilters = Object.entries(q).some(([k, v]) => k !== 'scope' && v !== undefined && v !== '');
    if (!hasFilters) {
      setCriteriaRequired(true);
      return;
    }
    setCriteriaRequired(false);
    const params = queryToUrlParams(q);
    // Re-running the identical search shouldn't stack duplicate history entries.
    if (params !== lastParamsRef.current) {
      lastParamsRef.current = params;
      setSearchParams(params ? new URLSearchParams(params) : {});
    }
    setQuery(q);
    setSearchedQuery(q);
    setHasSearched(true);
    // The contributions fetch backs the contributions-mode views (summary cards,
    // trend, contributor/committee context resolution). In expenditures mode it
    // would run UNSCOPED — payee/expenditure filters don't bound the contributions
    // table — so a payee search would drag a whole-cycle contributions scan behind
    // it. The expenditures views load their own data.
    if (q.mode !== 'expenditures') search(q);
  }, [search, setSearchParams]);

  // Browser back/forward: the URL changed underneath us — restore that entry's
  // search (or the empty pre-search state) instead of leaving it stale.
  useEffect(() => {
    const paramsStr = searchParams.toString();
    if (paramsStr === lastParamsRef.current) return; // our own write, not navigation
    lastParamsRef.current = paramsStr;
    const restored = urlParamsToQuery(paramsStr);
    setQuery(restored);
    setCriteriaRequired(false);
    const hasFilters = Object.entries(restored).some(([k, v]) => k !== 'scope' && v !== undefined && v !== '');
    if (hasFilters) {
      setSearchedQuery(restored);
      setHasSearched(true);
      if (restored.mode !== 'expenditures') search(restored);
    } else {
      setSearchedQuery({});
      setHasSearched(false);
    }
  }, [searchParams, search]);

  // Entity links everywhere (panel donors/recipients, dashboard lists, ledger
  // Source / Counterparty cells) launch a fresh search for that entity — exactly
  // as if the name were typed into the filter form. That makes the panel the
  // navigation map: contributor → committee → payee → committee…, each hop a real
  // search with its own history entry, so the browser Back button walks the chain.
  //
  // The click CARRIES the active analytical frame — window (cycle/dates/year) and
  // dollar bounds — so walking entities mid-investigation never silently jumps
  // cycles. Entity- and vocabulary-specific filters (types, names) reset: the
  // click pivots the question. A click with no window in context pins the current
  // cycle, matching a fresh form's default.
  const handleEntitySearch = useCallback((kind: 'contributor' | 'committee' | 'payee', name: string, meta?: { vendorId?: string | null }) => {
    const carried: CFQuery = {
      start_date: searchedQuery.start_date,
      end_date: searchedQuery.end_date,
      current_cycle: searchedQuery.current_cycle,
      year: searchedQuery.year,
      min_amount: searchedQuery.min_amount,
      max_amount: searchedQuery.max_amount,
    };
    if (!carried.start_date && !carried.end_date && !carried.year && cycle) {
      carried.current_cycle = true;
      carried.start_date = cycle.startDate;
      carried.end_date = cycle.endDate;
    }
    const q: CFQuery = kind === 'contributor'
      ? { ...carried, contributor: name }
      : kind === 'committee'
        ? { ...carried, committee_name: name }
        // Payee clicks carry the resolved vendor identity when the row had one, so
        // the vendor view lands exact and spelling-proof rather than name-matched.
        : { ...carried, mode: 'expenditures', payee: name, vendor_id: meta?.vendorId || undefined };
    doSearch(q);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [doSearch, cycle, searchedQuery]);

  const handleClearAll = useCallback(() => {
    const empty: CFQuery = {};
    setQuery(empty);
    setSearchedQuery(empty);
    setHasSearched(false);
    setCriteriaRequired(false);
    lastParamsRef.current = '';
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleApplySaved = useCallback((s: SavedCFSearch) => {
    const restored: CFQuery = {
      ...s.filters,
      searchQuery: s.searchQuery || undefined,
      scope: (s.scope as CFQuery['scope']) || 'any',
    };
    doSearch(restored);
    updateRun(s.id, s.resultCount || 0);
  }, [doSearch, updateRun]);

  const handleSaveFilter = useCallback(() => {
    setFilterName('');
    setShowSaveModal(true);
  }, []);

  const handleSaveFilterConfirm = useCallback(async () => {
    if (!filterName.trim()) return;
    try {
      setSavingFilter(true);
      await saveSearch(filterName.trim(), query);
      setShowSaveModal(false);
      setFilterName('');
    } catch (err: any) {
      window.alert(err.message || 'Failed to save filter');
    } finally {
      setSavingFilter(false);
    }
  }, [filterName, query, saveSearch]);

  const savePreviewItems: SaveFilterPreviewItem[] = useMemo(() => {
    const items: SaveFilterPreviewItem[] = [];
    const q = query;
    if (q.mode === 'expenditures') items.push({ label: 'Direction', value: 'Expenditures' });
    if (q.payee) items.push({ label: 'Payee', value: q.payee });
    if (q.expenditure_type) items.push({ label: 'Expenditure type', value: q.expenditure_type });
    if (q.disbursement_type) items.push({ label: 'Disbursement type', value: q.disbursement_type });
    if (q.searchQuery) items.push({ label: 'Search', value: `"${q.searchQuery}"` });
    if (q.scope && q.scope !== 'any') items.push({ label: 'Scope', value: q.scope });
    if (q.contributor) items.push({ label: 'Contributor', value: q.contributor });
    if (q.committee_name) items.push({ label: 'Committee', value: q.committee_name });
    if (q.candidate) items.push({ label: 'Candidate', value: q.candidate });
    if (q.office) items.push({ label: 'Office', value: q.office });
    if (q.party) items.push({ label: 'Party', value: q.party });
    if (q.office_chamber) items.push({ label: 'Chamber', value: q.office_chamber });
    if (q.contributor_type) items.push({ label: 'Contributor type', value: q.contributor_type });
    if (q.committee_type) items.push({ label: 'Committee type', value: q.committee_type });
    if (q.contribution_type) items.push({ label: 'Contribution type', value: q.contribution_type });
    if (q.employer) items.push({ label: 'Employer', value: q.employer });
    if (q.year) items.push({ label: 'Year', value: String(q.year) });
    if (q.current_cycle) {
      items.push({ label: 'Date range', value: 'Current cycle' });
    } else if (q.start_date || q.end_date) {
      items.push({ label: 'Date range', value: `${q.start_date || '…'} – ${q.end_date || '…'}` });
    }
    if (q.min_amount || q.max_amount) {
      items.push({ label: 'Amount', value: `${q.min_amount || '0'} – ${q.max_amount || '∞'}` });
    }
    if (q.group_by) items.push({ label: 'Grouped by', value: 'Contributor' });
    return items;
  }, [query]);

  // When a search targets a specific committee OR candidate, show the comprehensive
  // committee dashboard (header, KPIs, financial summary, follow-the-money, explorer)
  // in place of the general results. The committee CO_ID / name are resolved from the
  // first contribution row (TRACER contributions carry their committee's co_id + name).
  const committeeContext = hasSearched && !searchedQuery.contributor
    && (!!searchedQuery.committee_name || !!searchedQuery.candidate);
  const committeeCoId = committeeContext ? (contributions[0]?.co_id ?? null) : null;
  const committeeName = committeeContext
    ? (searchedQuery.committee_name || contributions[0]?.committee_name || searchedQuery.candidate || '')
    : '';
  const committeeParty = committeeContext ? (contributions[0]?.candidate_party ?? null) : null;
  // A contributor (donor) search gets its own outbound-style dashboard.
  const contributorContext = hasSearched && !committeeContext && !!searchedQuery.contributor;
  // A payee-centric search (no committee in context) lands on the vendor view.
  const vendorContext = hasSearched && !committeeContext && !contributorContext && mode === 'vendor';
  // `/campaign-finance?co_id=` — the committee card's "Open full dashboard" (PRD §5.12):
  // the dashboard for that TRACER committee mounts at once, no Search press. A later
  // search replaces the URL and takes over.
  const directCoId = !hasSearched ? (searchParams.get('co_id') || '').trim() || null : null;
  const cycleLabel = searchedQuery.current_cycle && cycle
    ? String(cycle.electionYear)
    : (searchedQuery.year ? String(searchedQuery.year) : undefined);

  return (
    <div className="space-y-6 pt-2">
      {/* Filters + My Filters grid (matching Legislation page layout) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <CFFilters
          query={query}
          onChange={setQuery}
          onSearch={doSearch}
          onClear={handleClearAll}
          onSaveFilter={handleSaveFilter}
        />
        <CFSavedFilters
          savedSearches={savedSearches}
          loading={savedLoading}
          onApply={handleApplySaved}
          onDelete={removeSearch}
        />
      </div>

      {/* Criteria required message */}
      {criteriaRequired && (
        <div className="card border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20">
          <p className="text-sm text-warning-800 dark:text-warning-200">Please enter at least one search criterion before searching.</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="card border-error-200 dark:border-error-800 bg-error-50 dark:bg-error-900/20">
          <p className="text-sm text-error-800 dark:text-error-200">{error}</p>
        </div>
      )}

      {directCoId && (
        <CFCommitteeDashboard
          key={directCoId}
          coId={directCoId}
          committeeName=""
          startDate={searchParams.get('start_date') || undefined}
          endDate={searchParams.get('end_date') || undefined}
          onEntitySearch={handleEntitySearch}
        />
      )}

      {/* Comprehensive committee dashboard — shown when a committee is in context. */}
      {committeeContext && (
        <CFCommitteeDashboard
          coId={committeeCoId}
          committeeName={committeeName}
          startDate={searchedQuery.start_date}
          endDate={searchedQuery.end_date}
          party={committeeParty}
          cycleLabel={cycleLabel}
          onEntitySearch={handleEntitySearch}
        />
      )}

      {/* Contributor (donor) dashboard — outbound sibling of the committee view. */}
      {contributorContext && (
        <CFContributorDashboard
          contributorName={searchedQuery.contributor!}
          summary={summary}
          topRecipients={topRecipients}
          monthlyTrend={monthlyTrend}
          query={searchedQuery}
          cycleLabel={cycleLabel}
          onEntitySearch={handleEntitySearch}
        />
      )}

      {/* Vendor (payee) landing — identity, who-paid-them, scoped ledger. */}
      {vendorContext && (
        <CFVendorDashboard
          payeeName={searchedQuery.payee || ''}
          vendorId={searchedQuery.vendor_id || null}
          query={searchedQuery}
          cycleLabel={cycleLabel}
          onEntitySearch={handleEntitySearch}
        />
      )}

      {/* General (free / non-entity) search results */}
      {!directCoId && !committeeContext && !contributorContext && !vendorContext && (
        <>
          {hasSearched && mode === 'general' && summary && summary.count > 0 && (
            <CFSummaryCards summary={summary} />
          )}

          {hasSearched && summary && summary.count > 0 && (
            <CFTrendChart data={monthlyTrend} title="Monthly Trend" />
          )}

          {/* Transactions explorer — same tabbed table as the committee view,
              scoped by the search filters (contributions / expenditures / loans). */}
          {hasSearched && (
            <CFExplorerPanel query={searchedQuery} cycleLabel={cycleLabel} onEntitySearch={handleEntitySearch} />
          )}
        </>
      )}

      {/* Footer */}
      {hasSearched && (
        <div className="text-center py-4">
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            Source: Colorado Secretary of State campaign finance filings · Capitol Commons
          </p>
        </div>
      )}

      <SaveFilterModal
        isOpen={showSaveModal}
        filterName={filterName}
        onFilterNameChange={setFilterName}
        onCancel={() => setShowSaveModal(false)}
        onSave={handleSaveFilterConfirm}
        saving={savingFilter}
        previewItems={savePreviewItems}
      />
    </div>
  );
};

export default CampaignFinancePage;
